import { Component, Output, Input, EventEmitter, ComponentRef, OnDestroy, OnInit, inject, ViewContainerRef, Injector, Type, signal, effect, EnvironmentInjector, createComponent, WritableSignal } from '@angular/core';

/* Components */
import { ModalComponents } from './components';

@Component({
  selector: 'app-modal-generator',
  template: '<ng-container #container></ng-container>',
})
export class ModalGeneratorComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  private environmentInjector = inject(EnvironmentInjector);
  private viewContainerRef = inject(ViewContainerRef);
  @Input() set content(value: string | undefined) {
    this._content.set(value || '');
  }
  @Output() loaded: EventEmitter<void> = new EventEmitter<void>();
  private _content: WritableSignal<string> = signal<string>('');
  private embeddedComponents: ComponentRef<any>[] = [];
  private componentsMap = new Map<string, Type<any>>();
  public data: any = [];

  private convertDynamicNodeValue(val: string): any {
    const sanitized = val.replace(/'/g, '"');
    return JSON.parse(sanitized);
  }

  private convertNodeName(name: string): string {
    const values: string[] = name.split('-');
    return values.reduce((result, current, index) => {
      if (index === 0) return current;
      return result + current.charAt(0).toUpperCase() + current.slice(1);
    }, '');
  }

  private buildComponents(content: string): void {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = content;

    const elements = Array.from(tempElement.querySelectorAll('*')).reverse();

    for (const element of elements) {
      const tagName = element.tagName.toLowerCase();
      const componentType = this.componentsMap.get(tagName);

      if (componentType) {
        this.viewContainerRef.clear();

        const componentRef = createComponent(componentType, {
          environmentInjector: this.environmentInjector,
          elementInjector: this.injector,
          projectableNodes: [Array.from(element.childNodes)],
        });

        Array.from(element.attributes).forEach((attr) => {
          const isDynamicInput = attr.name.startsWith('[') && attr.name.endsWith(']');

          if (isDynamicInput) {
            const inputName = this.convertNodeName(attr.name.substring(1, attr.name.length - 1));
            const inputValue = this.convertDynamicNodeValue(attr.value);
            if (inputValue) componentRef.setInput(inputName, inputValue);
          } else {
            componentRef.setInput(attr.name, attr.value);
          }
        });

        this.embeddedComponents.push(componentRef);

        this.viewContainerRef.insert(componentRef.hostView);
      }
    }
  }

  private clearComponents(): void {
    this.embeddedComponents.forEach((component) => component.destroy());
    this.embeddedComponents = [];
  }

  constructor() {
    effect(() => {
      const contentValue = this._content();
      if (contentValue) {
        this.clearComponents();
        this.buildComponents(contentValue);
        this.loaded.emit();
      }
    });
  }

  ngOnInit(): void {
    ModalComponents.forEach((component: any) => {
      const selector = component.ɵcmp?.selectors?.[0]?.[0] || '';
      if (selector) {
        this.componentsMap.set(selector, component);
      }
    });

    if (this._content()) {
      this.buildComponents(this._content());
      this.loaded.emit();
    }
  }

  ngOnDestroy(): void {
    this.clearComponents();
  }
}
